import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, ChevronRight, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Renders the 4 lesson modules with progress bars + the daily streak badge.
 * Used on the /learn page. Only visible to authenticated users.
 */
export default function LessonModulesGrid() {
  const { user } = useAuth();
  const [data, setData] = useState({ modules: [], streak: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api
      .get("/lessons/modules")
      .then((r) => setData(r.data))
      .catch(() => setData({ modules: [], streak: null }))
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div
        className="rounded-2xl bg-[#547A8F]/5 dark:bg-[#6E94A8]/10 border border-[#547A8F]/20 p-6 text-center text-sm text-[#2A2F2B] dark:text-[#E8E6E2]"
        data-testid="lesson-modules-guest"
      >
        <Link to="/login" className="text-[#547A8F] dark:text-[#87A8BA] font-semibold hover:underline">Sign in</Link>{" "}
        to unlock flashcard modules, daily streaks, and the chart sandbox.
      </div>
    );
  }

  return (
    <section data-testid="lesson-modules-grid">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
            Your modules
          </div>
          <h2 className="font-heading text-2xl text-[#2A2F2B] dark:text-[#E8E6E2] mt-0.5">
            Bite-sized flashcards
          </h2>
        </div>
        {data.streak && (
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D18E6315] text-[#D18E63] font-semibold text-sm"
            data-testid="learn-streak-badge"
            title={`${data.streak.streak}-day streak`}
          >
            <Flame className="w-4 h-4" strokeWidth={2} />
            {data.streak.streak} day{data.streak.streak === 1 ? "" : "s"}
          </div>
        )}
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl bg-white dark:bg-[#1F2421] border border-[#EAE6E1] dark:border-[#2C302E] h-32 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {data.modules.map((m) => {
            const pct = m.progress?.percent ?? 0;
            const completed = m.progress?.seen_card_ids?.length ?? 0;
            const total = m.progress?.total ?? 0;
            return (
              <Link
                key={m.slug}
                to="/dashboard"
                data-testid={`module-card-${m.slug}`}
                className="group rounded-2xl bg-white dark:bg-[#1F2421] border border-[#EAE6E1] dark:border-[#2C302E] p-5 transition-soft hover:-translate-y-1 hover:shadow-md"
              >
                <div className="flex items-start justify-between mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${m.color}20` }}
                  >
                    <Sparkles className="w-5 h-5" style={{ color: m.color }} strokeWidth={1.75} />
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#6B7068] group-hover:translate-x-0.5 transition-soft" />
                </div>
                <h3 className="font-heading text-lg text-[#2A2F2B] dark:text-[#E8E6E2]">{m.title}</h3>
                <p className="text-xs text-[#6B7068] mt-1 line-clamp-2">{m.summary}</p>
                <div className="mt-3 flex items-center justify-between text-xs font-mono-ticker">
                  <span className="text-[#6B7068]">{completed}/{total} cards</span>
                  <span className="font-semibold text-[#2A2F2B] dark:text-[#E8E6E2]">{pct}%</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-[#EAE6E1] dark:bg-[#2C302E] overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: m.color }}
                  />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
